"use client";

import { Button, buttonVariants } from "@/components/atoms/buttons";
import OrderDetailsCard from "@/components/modules/account/order-detail-card";
import OrderProductCard from "@/components/modules/account/order-product-card";
import OrderSummaryFooter from "@/components/modules/account/order-summery-footer";
import { Locales } from "@/lib/i18n";
import { __, getPermalink } from "@/lib/i18n/functions";
import { getOrderItemReference } from "@/lib/order-item-reference";
import { cn } from "@/lib/utils";
import { sdk } from "@/lib/medusa/client";
import OrderDetailsLoader from "@/components/modules/account/order-details-loader";
import { useAddToCart, useRemoveCartItem, useUpdateCartItem } from "@/hooks/useCartMutations";
import { Toast, toast } from "@/components/atoms/toast";
import { Loader2, ShoppingCart } from "lucide-react";
import Link from "next/link";
import { useCart } from "@/hooks/useCart";
import { usePrismicProducts, useProducts } from "@/hooks/useProduct";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useCustomer } from "@/hooks/useCustomer";
import { useRouter } from "next/navigation";

type AccountOrderDetailsProps = {
  locale: Locales;
  orderId: string;
};

const AccountOrderDetails = ({ locale, orderId }: AccountOrderDetailsProps) => {
  const { cart } = useCart();
  const { customer } = useCustomer();
  const router = useRouter();

  const { data: order, isLoading: isOrderLoading } = useQuery({
    queryKey: ["order", orderId],
    queryFn: async () => {
      const { order } = await sdk.store.order.retrieve(orderId, {
        fields:
          "+customer_id,metadata,created_at,*items,*shipping_methods,*billing_address,*shipping_address,*customer,item_total,item_subtotal,item_tax_total,total,subtotal,tax_total",
      });
      return order;
    },
    enabled: !!orderId,
  });

  useEffect(() => {
    if (!order?.customer?.id || !customer?.id) return;

    if (order.customer.id !== customer.id) {
      toast.error({
        title: __("This order isn't associated with your account. You've been redirected to your orders page.", locale),
      });
      router.push(getPermalink(locale, "account", "orders"));
    }
  }, [order, customer, router, locale]);

  const { data: medusaProducts, isLoading: isMedusaLoading } = useProducts(
    locale,
    order?.items?.map((item) => item.product_id || "") || []
  );

  const { data: prismicProducts, isLoading: isPrismicLoading } = usePrismicProducts(
    locale,
    order?.items?.map((item) => item.product_id || "") || []
  );

  const { addToCart, isLoading: isAddingAllToCart } = useAddToCart({
    onError: (error) => {
      console.error("Failed to add item to cart:", error);
      toast.error({ title: __("Failed to add item to cart", locale) });
    },
  });
  const { removeItem } = useRemoveCartItem();
  const { updateQuantity } = useUpdateCartItem();
  const productsArray = Object.values(medusaProducts || {});

  const orderItemVariantIds =
    order?.items
      ?.filter((item) => {
        const product = medusaProducts?.[item.product_id || ""];
        return product && product.status !== "draft";
      })
      .map((item) => item.variant_id)
      .filter((id): id is string => !!id) ?? [];

  const handleAddAll = async () => {
    try {
      const ops = orderItemVariantIds.map(async (variantId) => {
        const orderItem = order?.items?.find((item) => item.variant_id === variantId);
        const orderQty = orderItem?.quantity || 1;
        const productId = orderItem?.product_id;

        const sameVariantInCart = cart?.items?.find((i) => i.variant_id === variantId);
        const differentVariantInCart = cart?.items?.find(
          (i) => i.product_id === productId && i.variant_id !== variantId
        );

        if (sameVariantInCart) {
          // Same variant already in cart — merge quantities
          updateQuantity(sameVariantInCart.id, sameVariantInCart.quantity + orderQty);
        } else if (differentVariantInCart) {
          // Different variant of same product in cart — swap it out
          removeItem(differentVariantInCart.id);
          addToCart(variantId, orderQty);
        } else {
          addToCart(variantId, orderQty);
        }
      });

      await Promise.all(ops);
      toast.success({ title: __("All products added to cart!", locale) });
    } catch (err) {
      console.error("Error adding all items to cart:", err);
      toast.error({ title: __("Failed to add some products to cart", locale) });
    }
  };

  const isProductsInCart = orderItemVariantIds.every((id) => {
    return cart?.items && cart.items.some((item) => item.variant_id === id);
  });

  const isAllProductsOutOfStock = productsArray.every((product) => product.status === "draft");

  const isLoading = isOrderLoading || isMedusaLoading || isPrismicLoading;

  return (
    <article className={cn("text-dark-primary flex-1/2 space-y-10")}>
      {isLoading ? (
        <OrderDetailsLoader />
      ) : order ? (
        <>
          <OrderDetailsCard locale={locale} order={order} />
          <section className="flex flex-col gap-5">
            <h2 className="font-cabinet-grotesk text-2xl font-medium md:text-3xl">
              {__("Summary of your order", locale)}
            </h2>
            <ul className="space-y-[13px]">
              {order?.items?.map((item) => {
                const medusaProduct = medusaProducts?.[item.product_id || ""];

                return (
                  <OrderProductCard
                    product={medusaProduct}
                    key={item.id}
                    locale={locale}
                    prismicProduct={prismicProducts?.find((product) => product.data.medusaId === medusaProduct?.id)}
                    variant="secondary"
                    quantity={item.quantity}
                    unitPrice={item.unit_price ?? 0}
                    reference={getOrderItemReference({
                      snapshotSku: item.variant_sku,
                    })}
                  />
                );
              })}
            </ul>
            <OrderSummaryFooter locale={locale} order={order} />
            {order !== undefined &&
              (isAllProductsOutOfStock ? (
                <Toast
                  id="collection-out-of-stock"
                  type="info"
                  className="mt-3 max-w-none shadow-none"
                  title={__("All products are out of stock", locale)}
                />
              ) : isProductsInCart ? (
                <Link
                  href={getPermalink(locale, "cart")}
                  aria-label={__("Go to cart", locale)}
                  className={cn(
                    buttonVariants(),
                    "flex h-10 items-center justify-center rounded-xs p-0 px-2 text-sm transition-transform duration-150 ease-in-out active:scale-95 max-sm:w-full min-sm:ml-auto md:px-4 md:text-base"
                  )}
                >
                  {__("View Cart", locale)}
                </Link>
              ) : (
                <Button
                  disabled={isAddingAllToCart}
                  onClick={handleAddAll}
                  variant="primary"
                  className="h-11 text-base font-semibold min-sm:ml-auto min-sm:w-fit"
                  size="md"
                >
                  {isAddingAllToCart ? <Loader2 className="animate-spin" /> : <ShoppingCart aria-hidden="true" />}
                  {__("Order again", locale)}
                </Button>
              ))}
          </section>
        </>
      ) : (
        <p className="text-lg text-red-500">{__("Order not found", locale)}.</p>
      )}
    </article>
  );
};

export default AccountOrderDetails;
